<template>
  <!-- ==================== 일자 방문 그리드 모달 ==================== -->
  <ModalPopup :show="show" @update:show="$emit('update:show', $event)" @initialize="$emit('initialize')">
    <template #header>
      <h3>{{ title }}</h3>
    </template>
    <template #body>
      <div v-if="loading" class="cal-modal__state">{{ t('dashboard.calendar.modal.loading') }}</div>
      <div v-else-if="!visits.length" class="cal-modal__state cal-modal__state--empty">
        {{ t('dashboard.calendar.modal.empty') }}
      </div>
      <div v-else class="cal-modal__table-wrap">
        <table class="cal-modal__table">
          <thead>
            <tr>
              <th>{{ t('dashboard.calendar.modal.requestId') }}</th>
              <th>{{ t('dashboard.calendar.modal.visitTime') }}</th>
              <th>{{ t('dashboard.calendar.modal.processStatus') }}</th>
              <th>{{ t('dashboard.calendar.modal.partnerName') }}</th>
              <th>{{ t('dashboard.calendar.modal.customerName') }}</th>
              <th>{{ t('dashboard.calendar.modal.productGroup') }}</th>
              <th>{{ t('dashboard.calendar.modal.symptom') }}</th>
              <th>{{ t('dashboard.calendar.modal.technicianName') }}</th>
              <th>{{ t('dashboard.calendar.modal.contact') }}</th>
              <th>{{ t('dashboard.calendar.modal.region') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="visit in visits"
              :key="visit.requestId"
              class="cal-modal__row"
              tabindex="0"
              role="button"
              :aria-label="t('dashboard.calendar.modal.rowAriaLabel', { id: visit.requestId })"
              @click="onRowClick(visit)"
              @keydown.enter.prevent="onRowClick(visit)"
              @keydown.space.prevent="onRowClick(visit)"
            >
              <td>{{ visit.requestId }}</td>
              <td>{{ visit.visitTime }}</td>
              <td>{{ processStatusLabel(visit.processStatus) }}</td>
              <td>{{ visit.partnerName }}</td>
              <td>{{ visit.customerName }}</td>
              <td>{{ visit.productGroup }}</td>
              <td>{{ visit.symptom }}</td>
              <td>{{ visit.technicianName }}</td>
              <td>{{ visit.contact }}</td>
              <td>{{ visit.region }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </template>
    <template #footer>
      <button type="button" class="rd-btn-outline m-btn m-btn--outline" @click="$emit('update:show', false)">
        {{ t('button.close') }}
      </button>
    </template>
  </ModalPopup>
</template>

<script setup>
/* global defineProps, defineEmits */
import { nextTick } from 'vue'
import { useRouter } from 'vue-router'
import i18n from '@/locales/i18n'
import ModalPopup from '@/components/common/ModalPopup.vue'

const t = i18n.global.t
const router = useRouter()

defineProps({
  show: { type: Boolean, default: false },
  title: { type: String, default: '' },
  visits: { type: Array, default: () => [] },
  loading: { type: Boolean, default: false }
})

const emit = defineEmits(['update:show', 'initialize'])

/**
 * visit 행 클릭 → 모달 close + 해당 접수 상세 페이지로 직진 (SURINP-290).
 * RepairRequestManagementDetail.vue 는 `$route.params.id`를 사용하므로
 * params 기반 path(`/repair-request-management-detail/:id/:partnerId`)로 push.
 * partnerId가 visit 객체에 없으면 navigation 포기하고 로그 기록 (params 누락 시 라우트 매칭 실패).
 *
 * @param {{requestId: string|number, partnerId?: string|number}} visit
 */
async function onRowClick(visit) {
  if (!visit || visit.requestId == null) return
  if (visit.partnerId == null) {
    window.__APP_LOG__('[TechnicianCalendarDayModal] partnerId 누락 — 상세 페이지 이동 불가', visit)
    return
  }
  // 부모 modalOpen이 false로 reactive flush 된 후 라우팅해야 KeepAlive 캐시에
  // modalOpen=false 상태로 저장되어 뒤로가기 시 모달 재오픈을 막을 수 있다.
  emit('update:show', false)
  await nextTick()
  router.push({
    path: `/repair-request-management-detail/${visit.requestId}/${visit.partnerId}`
  })
}

/**
 * 백엔드 enum(`VISIT_PLANNED` / `IN_PROGRESS` / `COMPLETED` 등)을
 * 사용자 언어 라벨로 변환한다. dashboard.calendar.processStatus.{CODE} 키가 없으면
 * raw 코드를 그대로 노출(향후 백엔드에 새 상태 추가 시 빈 셀 방지).
 *
 * vue-i18n legacy 모드는 missing 키일 때 키 자체를 반환하므로 그 동작에 의존한다.
 * (`i18n.global.te`는 일부 테스트용 i18n 인스턴스에서 노출되지 않아 사용하지 않음)
 *
 * @param {string|null|undefined} code
 * @returns {string}
 */
function processStatusLabel(code) {
  if (!code) return ''
  const key = `dashboard.calendar.processStatus.${code}`
  const label = t(key)
  return label === key ? code : label
}
</script>

<style scoped>
.cal-modal__state {
  padding: 32px 16px;
  text-align: center;
  font-size: 13px;
  color: var(--color-gray-500, #5C616B);
}

.cal-modal__state--empty {
  font-style: italic;
}

.cal-modal__table-wrap {
  overflow-x: auto;
  max-height: 60vh;
}

.cal-modal__table {
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
}

.cal-modal__table th,
.cal-modal__table td {
  padding: 8px 10px;
  border-bottom: 1px solid var(--color-gray-100, #E8EAED);
  text-align: left;
  white-space: nowrap;
}

.cal-modal__table th {
  background: var(--color-gray-50, #F3F4F6);
  font-weight: 700;
  color: var(--color-gray-700, #2E333B);
  position: sticky;
  top: 0;
  z-index: 1;
}

/* 행 클릭 → 접수 상세 직진 (SURINP-290) */
.cal-modal__row {
  cursor: pointer;
  transition: background-color 0.12s;
}

.cal-modal__row:hover {
  background-color: var(--color-gray-50, #F9FAFB);
}

.cal-modal__row:focus-visible {
  background-color: var(--color-gray-50, #F9FAFB);
  outline: 2px solid var(--color-primary-500, #3B5998);
  outline-offset: -2px;
}
</style>
